// Notification service for prayer times

// Check if browser notifications are supported
export const isNotificationsSupported = () => {
  return 'Notification' in window;
};

// Request permission to show notifications
export const requestNotificationPermission = async () => {
  if (!isNotificationsSupported()) {
    return { granted: false, message: 'Notifications are not supported in this browser.' };
  }

  try {
    const permission = await Notification.requestPermission();
    return { 
      granted: permission === 'granted',
      message: permission === 'granted' 
        ? 'Notifications enabled successfully!' 
        : 'You need to allow notifications to receive prayer time alerts.'
    };
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return { granted: false, message: 'Failed to request notification permission.' };
  }
};

// Check current notification permission status
export const getNotificationPermissionStatus = () => {
  if (!isNotificationsSupported()) {
    return 'unsupported';
  }
  return Notification.permission; // 'granted', 'denied', or 'default'
};

// Show a prayer time notification with support for reminder text
export const showPrayerNotification = (prayerName, prayerTime, playSound = true, soundPath = '', isReminder = false, reminderMinutes = 10) => {
  if (!isNotificationsSupported() || Notification.permission !== 'granted') {
    return false;
  }

  try {
    const title = isReminder 
      ? `Prepare for ${prayerName} Prayer`
      : `${prayerName} Prayer Time`;
      
    const body = isReminder
      ? `${prayerName} prayer will start in ${reminderMinutes} minutes (${prayerTime})`
      : `It's time for ${prayerName} (${prayerTime})`;
      
    const options = {
      body: body,
      icon: '/favicon.svg',
      badge: '/favicon.svg',
      vibrate: [200, 100, 200],
      tag: isReminder ? `prayer-reminder-${prayerName.toLowerCase()}` : 'prayer-time',
      renotify: true,
      requireInteraction: !isReminder // Only main prayer notification requires interaction
    };
    
    const notification = new Notification(title, options);
    
    notification.onclick = () => {
      window.focus();
      notification.close();
      window.location.href = '/prayer-times';
    };
    
    if (playSound && soundPath) {
      playAthanSound(soundPath);
    }
    
    return true;
  } catch (error) {
    console.error('Error showing notification:', error);
    return false;
  }
};

// Play athan sound with better error handling for online files
export const playAthanSound = (soundPath) => {
  try {
    // Fallback to a reliable online source if the path is missing or invalid
    const audioUrl = soundPath || 'https://islamcan.com/audio/adhan/azan1.mp3';
    
    const audio = new Audio(audioUrl);
    audio.volume = 1.0; // Full volume for Athan, will be adjusted by settings
    
    // Add error handling for network issues
    audio.onerror = (e) => {
      console.error('Error loading Athan audio:', e);
      // Try fallback if primary source fails
      if (audioUrl !== 'https://islamcan.com/audio/adhan/azan1.mp3') {
        console.log('Attempting to play fallback Athan...');
        const fallbackAudio = new Audio('https://islamcan.com/audio/adhan/azan1.mp3');
        fallbackAudio.play().catch(err => {
          console.error('Fallback Athan also failed:', err);
        });
        return fallbackAudio;
      }
    };
    
    // Add event listener for when audio is ready
    audio.oncanplaythrough = () => {
      console.log('Athan audio loaded and ready to play');
    };
    
    // Attempt to play audio
    const playPromise = audio.play();
    
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          // Audio playback started successfully
          console.log('Playing Athan');
        })
        .catch(error => {
          // Auto-play was prevented
          console.error('Error playing Athan (likely autoplay restricted):', error);
          
          // Create a UI notification that user interaction is required for autoplay
          showPrayerNotification(
            'Prayer Time', 
            'Click here to play Athan', 
            false, 
            ''
          );
        });
    }
    
    return audio;
  } catch (error) {
    console.error('Failed to play Athan:', error);
    return null;
  }
};

// Schedule notifications for all prayer times with support for personalized reminders
export const schedulePrayerNotifications = (prayerTimes, soundSettings) => {
  if (!prayerTimes || !Array.isArray(prayerTimes) || prayerTimes.length === 0) {
    return [];
  }

  const now = new Date();
  const scheduledNotifications = [];
  
  prayerTimes.forEach(prayer => {
    const prayerDateTime = new Date(prayer.datetime);
    
    // Only schedule if prayer time is in the future
    if (prayerDateTime > now) {
      const msUntilPrayer = prayerDateTime - now;
      const prayerName = prayer.name.toLowerCase();
      
      // Get appropriate sound path based on prayer name and settings
      const soundPath = getSoundPathForPrayer(prayer.name, soundSettings);
      
      // Schedule the main prayer notification
      const timerId = setTimeout(() => {
        showPrayerNotification(
          prayer.name, 
          prayer.time, 
          soundSettings.enabled, 
          soundPath
        );
      }, msUntilPrayer);
      
      scheduledNotifications.push({
        id: timerId,
        prayer: prayer.name,
        time: prayer.time,
        datetime: prayerDateTime,
        type: 'main'
      });
      
      // Schedule pre-prayer reminder if enabled
      if (soundSettings.customRemindersEnabled && 
          soundSettings.customReminderTimes && 
          soundSettings.customReminderTimes[prayerName]) {
        
        const reminderMinutes = soundSettings.customReminderTimes[prayerName];
        const msUntilReminder = msUntilPrayer - (reminderMinutes * 60 * 1000);
        
        // Only schedule reminder if it's in the future
        if (msUntilReminder > 0) {
          const reminderId = setTimeout(() => {
            showPrayerNotification(
              prayer.name,
              prayer.time,
              false, // Don't play Athan sound for reminder
              ''
            );
          }, msUntilReminder);
          
          scheduledNotifications.push({
            id: reminderId,
            prayer: prayer.name,
            time: prayer.time,
            datetime: new Date(prayerDateTime - (reminderMinutes * 60 * 1000)),
            type: 'reminder',
            minutesBefore: reminderMinutes
          });
        }
      }
    }
  });
  
  return scheduledNotifications;
};

// Clear scheduled notifications
export const clearScheduledNotifications = (scheduledNotifications) => {
  if (!scheduledNotifications || !Array.isArray(scheduledNotifications)) {
    return;
  }
  
  scheduledNotifications.forEach(notification => {
    if (notification && notification.id) {
      clearTimeout(notification.id);
    }
  });
};

// Get the appropriate sound file path based on prayer name and settings
const getSoundPathForPrayer = (prayerName, soundSettings) => {
  if (!soundSettings.enabled) {
    return '';
  }
  
  const prayerLower = prayerName.toLowerCase();
  
  // Use special Fajr sound if enabled and it's Fajr prayer
  if (prayerLower === 'fajr' && soundSettings.specialFajrAthan) {
    return soundSettings.fajrAthanPath || 'https://islamcan.com/audio/adhan/fajr.mp3';
  }
  
  // Otherwise use the default athan sound
  return soundSettings.athanPath || 'https://islamcan.com/audio/adhan/azan1.mp3';
};

// Create named service object
const notificationService = {
  isNotificationsSupported,
  requestNotificationPermission,
  getNotificationPermissionStatus,
  showPrayerNotification,
  playAthanSound,
  schedulePrayerNotifications,
  clearScheduledNotifications
};

export default notificationService;